import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { verifyEmail } from '../api/userApi.js';

// Email 驗證信連結的落地頁：/verify-email?token=xxx
// 進頁就把 token 送回後端完成驗證，顯示結果。
function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { user, setUser } = useAuth();

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');
  const ran = useRef(false); // 防 StrictMode 重複呼叫，token 是一次性的

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setStatus('error');
      setMessage('缺少驗證連結，請從信件中的連結進入。');
      return;
    }

    verifyEmail(token)
      .then(() => {
        setStatus('success');
        // 若本人已登入，立即把狀態更新成已驗證，讓提醒橫幅消失
        if (user) setUser({ ...user, emailVerified: true });
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || '驗證失敗，連結可能無效或已過期。');
      });
  }, [token, user, setUser]);

  return (
    <main style={{ maxWidth: 520, margin: '0 auto', padding: '64px 20px 80px', textAlign: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: 'var(--shadow-sm)', padding: '40px 28px' }}>
        {status === 'verifying' && (
          <>
            <h1 style={{ font: '900 24px "Noto Sans TC", sans-serif', color: 'var(--navy)', margin: 0 }}>驗證中…</h1>
            <p style={{ color: 'var(--ink-soft)', marginTop: 12 }}>正在確認你的 Email，請稍候。</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--mint)', color: 'var(--mint-deep)', display: 'grid', placeItems: 'center', margin: '0 auto 20px' }}>
              <Icon name="check" size={36} />
            </div>
            <h1 style={{ font: '900 24px "Noto Sans TC", sans-serif', color: 'var(--navy)', margin: 0 }}>Email 驗證成功！</h1>
            <p style={{ color: 'var(--ink-soft)', marginTop: 12, lineHeight: 1.7 }}>
              你的帳號已完成驗證，現在可以正常下單與送出代打登記了。
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
              <Link className="btn btn-primary" to="/" style={btnStyle('primary')}>回首頁 <Icon name="arrow" size={14} /></Link>
              <Link className="btn" to="/virtual" style={btnStyle('ghost')}>逛逛虛擬商品</Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FCEDED', color: 'var(--red)', display: 'grid', placeItems: 'center', margin: '0 auto 20px' }}>
              <Icon name="shield" size={34} />
            </div>
            <h1 style={{ font: '900 24px "Noto Sans TC", sans-serif', color: 'var(--navy)', margin: 0 }}>驗證沒有成功</h1>
            <p style={{ color: 'var(--ink-soft)', marginTop: 12, lineHeight: 1.7 }}>{message}</p>
            <p style={{ color: 'var(--ink-soft)', marginTop: 4, fontSize: 13 }}>
              {user ? '可到會員中心或結帳頁，點「重寄驗證信」再試一次。' : '請先登入，再從會員頁重寄驗證信。'}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
              <Link className="btn btn-primary" to={user ? '/account' : '/login'} style={btnStyle('primary')}>
                {user ? '前往會員中心' : '前往登入'} <Icon name="arrow" size={14} />
              </Link>
              <Link className="btn" to="/" style={btnStyle('ghost')}>回首頁</Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}

function btnStyle(kind) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '12px 22px', borderRadius: 999,
    font: '800 14px "Noto Sans TC", sans-serif', textDecoration: 'none', cursor: 'pointer',
  };
  return kind === 'primary'
    ? { ...base, background: 'var(--navy)', color: '#fff' }
    : { ...base, background: '#fff', color: 'var(--navy)', border: '1.5px solid var(--line)' };
}

export default VerifyEmailPage;
